/* What somebody can open, and the two controls that change it.
 *
 * This was written inside LifecycleDrawer, as step 3 of onboarding, and for a
 * while that was the only place in the app where access could be read or set.
 * It made sense while the only people whose access anybody touched were the
 * ones being onboarded. It stopped making sense immediately: access changes all
 * the time for people who have been here years, and the only route to it was a
 * screen called Onboarding. Simon found it by asking why he had to go to
 * Onboarding to see what somebody could open.
 *
 * So it lives here, and both screens render it — the lifecycle flow as its
 * access step, and the Accounts drawer as a section. One component rather than
 * two, because two would be two answers to "what does this person hold", and
 * the second one to be written would be the one nobody remembered to update.
 *
 * It owns its own query and both mutations. A caller that also needs the
 * reading — LifecycleDrawer does, to work out the step's state — asks for it
 * with the same query key and react-query hands both the one request.
 */

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { APPS, HUB_LEVEL_LABELS, type AppRole, type HubLevel } from '@hird/access';
import { Button, Drawer, useToast, type ToastHandle } from '../ui';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import s from './screens.module.scss';

/* Plain tier names. Lifted from Hub Admin, where they read well enough that
   changing them here would only make the two disagree. */
const ROLE_LABEL: Record<string, string> = {
  none: 'No access',
  user: 'User',
  admin: 'Admin',
  superadmin: 'Super Admin',
};

export interface AccessPanelProps {
  /** The Entra object id. Absent for anybody with no account to grant. */
  graphId?: string;
  /** Their name, for the note shown when there is no account. */
  name?: string;
  /* Graph's `mail`, for the Training Matrix's employee row.
     NOT the UPN. At Hird you sign in as firstname.lastname@ and receive mail
     as initials@, and SharePoint's user list holds the latter — so matching
     on upn would find nobody and report it as "not in the employee list",
     which is the wrong answer in the most convincing possible way. */
  email?: string;
}

/* The app whose Admin tier carries a scope. The other apps' tiers are a group
   and nothing else; this one also needs to know which departments. */
const SCOPED_APP = 'trainingmatrix';

export function AccessPanel({ graphId, name, email }: AccessPanelProps) {
  const svc = useServices();
  const qc = useQueryClient();
  const [wantLevel, setWantLevel] = useState('');

  /* Live, never the nightly sync's copy. A badge can be a day old; a decision
     cannot, and this is the thing decisions are made from. */
  const directory = useQuery({
    queryKey: qk.directoryAccess(graphId ?? ''),
    queryFn: () => svc.directory.get(graphId as string),
    enabled: !!graphId,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.directoryAccess(graphId ?? '') });
    void qc.invalidateQueries({ queryKey: qk.audit });
  };

  /* Every write here is a group change in Entra and takes a second or two.
     The toast is raised the moment it starts and becomes its own result in
     place, because a control that merely goes quiet looks the same as one
     that is broken — and the answer to that is to press it again, which is
     the one thing a group write should never have to survive. */
  const toast = useToast();
  const levelToast = useRef<ToastHandle | null>(null);
  const appToast = useRef<ToastHandle | null>(null);
  const scopeToast = useRef<ToastHandle | null>(null);
  /* A name is singular and the fallback is not, so the verb has to move with
     it — "Simon Ford are Super Admin" is what you get for picking one. */
  const they = name || 'They';
  const is = name ? 'is' : 'are';
  const has = name ? 'has' : 'have';

  const setLevel = useMutation({

    meta: { quiet: true },
    mutationFn: (level: HubLevel | null) => svc.directory.setLevel(graphId as string, level),
    onMutate: () => {
      levelToast.current = toast(`Setting the hub level for ${name || 'them'}…`, 'busy');
    },
    onSuccess: (reading) => {
      levelToast.current?.update(
        reading.level
          ? `${they} ${is} now ${HUB_LEVEL_LABELS[reading.level]}. It takes about a minute to reach them.`
          : `${they} no longer ${has} hub access.`,
        'ok',
      );
      invalidate();
      setWantLevel('');
    },
    onError: (e) =>
      levelToast.current?.update(`Could not set the level: ${(e as Error).message}`, 'error'),
  });

  /* The Training Matrix's departments, and what this person administers.
     Only fetched once they actually hold Admin there — nobody else needs it,
     and an unconfigured build has no service to ask. */
  const scopeSvc = svc.trainingScope;
  const tmGrant = directory.data?.grants.find((g) => g.appId === SCOPED_APP);
  const isTmAdmin = (tmGrant?.override ?? tmGrant?.effective) === 'admin';
  const who = (email || '').trim();

  const scope = useQuery({
    queryKey: qk.trainingScope(who),
    queryFn: () => scopeSvc!.get(who, name),
    enabled: !!scopeSvc && isTmAdmin && !!who,
  });

  const departments = useQuery({
    queryKey: qk.trainingDepartments,
    queryFn: () => scopeSvc!.departments(),
    enabled: !!scopeSvc && isTmAdmin,
  });

  const [picked, setPicked] = useState<string[] | null>(null);
  const [scopeOpen, setScopeOpen] = useState(false);
  const scopeNow = typeof scope.data === 'object' ? scope.data : undefined;
  /* The boxes start from what they hold, and only become local state once
     somebody ticks one — so a refetch does not fight an edit in progress. */
  const ticked = picked ?? scopeNow?.departments ?? [];

  const saveScope = useMutation({

    meta: { quiet: true },
    mutationFn: () => scopeSvc!.set(scopeNow!.itemId, ticked),
    onMutate: () => {
      scopeToast.current = toast('Saving their departments…', 'busy');
    },
    onSuccess: (_r, _v, _c) => {
      const n = ticked.length;
      scopeToast.current?.update(
        `${they} can now see ${n === 1 ? ticked[0] : `${n} departments`} in the Training Matrix.`,
        'ok',
      );
      setPicked(null);
      /* Closing on success is the point of a tray: choose, save, gone. It
         stays open on failure, because the ticks are the only copy of what
         was being asked for. */
      setScopeOpen(false);
      void qc.invalidateQueries({ queryKey: qk.trainingScope(who) });
    },
    onError: (e) =>
      scopeToast.current?.update(
        `Could not save the departments: ${(e as Error).message}`,
        'error',
      ),
  });

  /* What the row under the Training Matrix says before the tray is opened.
     A refusal is said here rather than hidden behind a button, because it is
     the answer: there is nothing to open. */
  const scopeReady = !!scopeNow;
  const scopeSummary = !scopeSvc
    ? 'This build has not been told where the Training Matrix lives, so departments cannot be set here.'
    : !who
      ? `${they} ${has} no email address on their record, which is what the Training Matrix matches people on.`
      : scope.isPending
        ? 'Reading the Training Matrix…'
        : scope.data === 'no-employee-row'
          ? `${they} ${is} not in the Training Matrix's employee list, so there is no row to scope. Add them there first.`
          : scope.data === 'row-has-no-account'
            ? 'Their row in the Training Matrix is not linked to an account. Set the User column on that row first.'
            : scopeNow && scopeNow.departments.length
              ? `Sees ${scopeNow.departments.join(', ')}`
              : 'No departments chosen yet, so they can see nobody.';

  const setApp = useMutation({

    meta: { quiet: true },
    mutationFn: ({ appId, role }: { appId: string; role: AppRole | null; appName: string }) =>
      svc.directory.setAppRole(graphId as string, appId, role),
    onMutate: (v) => {
      appToast.current = toast(`Setting ${v.appName}…`, 'busy');
    },
    onSuccess: (_r, v) => {
      appToast.current?.update(
        v.role === null
          ? `${v.appName} now follows their hub level.`
          : `${they} ${is} ${ROLE_LABEL[v.role]} in ${v.appName}. It takes about a minute to reach them.`,
        'ok',
      );
      invalidate();
    },
    onError: (e, v) =>
      appToast.current?.update(`Could not set ${v.appName}: ${(e as Error).message}`, 'error'),
  });

  /* A row on the register with no Entra account behind it: a starter added by
     hand before the sync has seen them, a shared mailbox, a service account.
     Said plainly rather than shown as an empty grid, because an empty grid
     reads as "holds nothing" when the truth is "there is nothing to hold it". */
  if (!graphId) {
    return (
      <div className={s.ownedNote}>
        <i className="ti ti-info-circle" aria-hidden />
        <span>
          {name ? `${name} has` : 'This record has'} no Microsoft 365 account yet, so there is
          nothing to grant. Access can be set once the nightly sync has matched them to one.
        </span>
      </div>
    );
  }

  if (directory.isError) {
    return (
      <div className={s.stepError}>
        Could not read their access: {String((directory.error as Error).message)}
      </div>
    );
  }

  const reading = directory.data;

  return (
    <>
      <select
        className={s.stepSelect}
        value={wantLevel || String(reading?.level ?? '')}
        onChange={(e) => setWantLevel(e.target.value)}
        aria-label="Hub level"
        disabled={directory.isLoading}
      >
        <option value="">No access</option>
        {/* The levels the model actually has. This listed 1 to 5 until
            07/10/2026 and kept doing so after the model dropped to two, which
            rendered three blank options — HUB_LEVEL_LABELS had no entry for
            them. */}
        {(Object.keys(HUB_LEVEL_LABELS) as unknown as HubLevel[]).map((l) => (
          <option key={l} value={l}>{HUB_LEVEL_LABELS[l]}</option>
        ))}
      </select>
      <Button
        disabled={setLevel.isPending || wantLevel === ''}
        onClick={() => setLevel.mutate(wantLevel === '' ? null : (Number(wantLevel) as HubLevel))}
      >
        {setLevel.isPending ? 'Setting…' : 'Set the level'}
      </Button>

      {setLevel.error ? (
        <div className={s.stepError}>{String((setLevel.error as Error).message)}</div>
      ) : null}

      {/* Every app, and what they have in it.
       *
       * Here rather than in Hub Admin because Hub Admin no longer writes access
       * at all — two screens writing the same Entra groups is one too many.
       *
       * The level grants a baseline and an explicit choice overrules it in
       * either direction: somebody can be given an app their level does not
       * include, or held out of one it does. */}
      {reading ? (
        <div className={s.appGrants}>
          <div className={s.appGrantsTitle}>What they can open</div>
          {APPS.map((app) => {
            const grant = reading.grants.find((g) => g.appId === app.appId);
            const o = app.overrides || {};
            /* Only the tiers this app has. Offering Admin on an app with no
               admin group writes nothing and reports success, which is the
               fault the old hardcoded L5 had. */
            const tiers = (['user', 'admin', 'superadmin'] as AppRole[])
              .filter((r) => !!o[r as 'user' | 'admin' | 'superadmin']);
            const current = grant?.override ?? '';
            /* Which row is working, not merely which rows are unavailable.
               All ten selects disable together while one writes — that part is
               right, two group writes at once on one person is a race — but
               without this nothing says WHICH was chosen, so the whole list
               just goes dead for a second. */
            const busy = setApp.isPending && setApp.variables?.appId === app.appId;
            /* The app and its scope are one unit, so the rule that separates
               apps belongs below the pair. On the row itself it drew a line
               between the Training Matrix and its own departments, which read
               as a divider between two unrelated things. */
            return (
              <div key={app.appId} className={s.appGrantGroup}>
                <div className={s.appGrantRow}>
                  <div className={s.appGrantName}>
                    {app.appName}
                    <span className={s.appGrantFrom}>
                      {grant?.override
                        ? 'set for them'
                        : grant?.effective === 'none'
                          ? 'not included at their level'
                          : `${grant?.effective} by level`}
                    </span>
                  </div>
                  <span className={s.appGrantAction}>
                    {busy && <i className={`ti ti-loader-2 ${s.appGrantSpin}`} aria-hidden />}
                    <select
                      className={s.appGrantSelect}
                      value={current}
                      disabled={setApp.isPending}
                      aria-label={`${app.appName} access`}
                      onChange={(e) =>
                        setApp.mutate({
                          appId: app.appId,
                          appName: app.appName,
                          role: e.target.value === '' ? null : (e.target.value as AppRole),
                        })
                      }
                    >
                      <option value="">Follows their level</option>
                      {tiers.map((r) => (
                        <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                      ))}
                      {o.none ? <option value="none">No access</option> : null}
                    </select>
                  </span>
                </div>

                {/* Under the app it belongs to, not at the foot of the list.
                    The Training Matrix's Admin tier is the only one that
                    carries a scope — User sees their own records, SuperAdmin
                    sees every department — so the question belongs against
                    that row, where the tier that raised it was chosen. */}
                {app.appId === SCOPED_APP && isTmAdmin && (
                  <div className={s.scopeRow}>
                    <span className={s.scopeRowText}>{scopeSummary}</span>
                    {scopeReady && (
                      <button
                        type="button"
                        className={s.scopeOpen}
                        onClick={() => setScopeOpen(true)}
                      >
                        {scopeNow && scopeNow.departments.length ? 'Change' : 'Choose'} departments
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}

        </div>
      ) : null}

      {setApp.error ? (
        <div className={s.stepError}>{String((setApp.error as Error).message)}</div>
      ) : null}

      <div className={s.ownedNote}>
        These are Entra groups, so they decide what somebody can open in the app itself, not just
        whether the tile appears. A change takes about a minute to reach them.
      </div>

      {/* A tray over the person, rather than ten checkboxes wedged into a row
          of selects. Choose, save, gone — and the panel behind it keeps its
          place, so this is not a detour away from what you were doing.
          Drawers nest safely now; see the stack in components/ui. */}
      <Drawer
        open={scopeOpen && isTmAdmin && scopeReady}
        title="Departments"
        sub={`Which departments ${name || 'they'} can see in the Training Matrix`}
        avatar={<i className="ti ti-building-community" aria-hidden />}
        onClose={() => setScopeOpen(false)}
        footer={
          <div className={s.scopeFoot}>
            <Button
              /* Empty is refused here AND in the service. An Admin with no
                 departments can see nothing, which is a tier change wearing a
                 scope change's clothes. */
              disabled={!ticked.length || saveScope.isPending || picked === null}
              onClick={() => saveScope.mutate()}
            >
              {saveScope.isPending ? 'Saving…' : 'Save departments'}
            </Button>
            <span className={s.scopeNote}>
              {!ticked.length
                ? 'Pick at least one, or change their access level instead.'
                : `${ticked.length} of ${(departments.data ?? []).length}`}
            </span>
          </div>
        }
      >
        {departments.isPending ? (
          <div className={s.scopeNote}>Reading the Training Matrix…</div>
        ) : (
          <div className={s.scopeList}>
            {(departments.data ?? []).map((d) => {
              const on = ticked.indexOf(d) >= 0;
              return (
                <label key={d} className={s.scopeItem}>
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={saveScope.isPending}
                    onChange={() =>
                      setPicked(on ? ticked.filter((x) => x !== d) : ticked.concat([d]))
                    }
                  />
                  <span>{d}</span>
                </label>
              );
            })}
          </div>
        )}

        {saveScope.error ? (
          <div className={s.stepError}>{String((saveScope.error as Error).message)}</div>
        ) : null}
      </Drawer>
    </>
  );
}
